import React from 'react';
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine,
} from 'recharts';

// Artifact emitted by the project_wealth tool (kind: "wealth_projection").
export interface WealthProjectionData {
  currency: string;
  horizon_years: number;
  starting_value: number;
  expected_return: number;
  volatility: number;
  monthly_contribution: number;
  goal: number | null;
  probability_of_goal: number | null;
  years: number[];
  percentiles: {
    p10: number[];
    p25: number[];
    p50: number[];
    p75: number[];
    p90: number[];
  };
  invested: number[];
  total_invested: number;
  total_contributions: number;
  terminal: { p10: number; p50: number; p90: number; mean: number; median_growth: number };
  assumption?: {
    expected_return: number;
    expected_return_source?: string;
    volatility_source?: string;
  };
}

const NAVY = '#000537';
const GREY = '#a1a1aa';
const BAND = '#000537';

const money = (v: number, currency: string) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(v);

// Compact axis labels: €1.2M, €340k.
const compact = (v: number, currency: string) => {
  const sym = new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 })
    .format(0).replace(/[\d.,\s]/g, '');
  if (Math.abs(v) >= 1_000_000) return `${sym}${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `${sym}${Math.round(v / 1_000)}k`;
  return `${sym}${Math.round(v)}`;
};

const yearLabel = (y: number) => (y === 0 ? 'now' : `${y}y`);

const WealthProjectionChart: React.FC<{ data: WealthProjectionData }> = ({ data }) => {
  const { currency, percentiles, years, goal, terminal } = data;
  const p = percentiles || ({} as WealthProjectionData['percentiles']);

  // Stack the band as base (p10) + height (p90 - p10) so the Area paints only the
  // p10–p90 region, with the median drawn as a line on top.
  const invested = data.invested || [];
  const rows = (years || []).map((yr, i) => ({
    year: yr,
    p10: p.p10?.[i] ?? 0,
    band: (p.p90?.[i] ?? 0) - (p.p10?.[i] ?? 0),
    p25: p.p25?.[i] ?? 0,
    p50: p.p50?.[i] ?? 0,
    p75: p.p75?.[i] ?? 0,
    p90: p.p90?.[i] ?? 0,
    invested: invested[i] ?? null,
  }));
  const hasContributions = (data.total_contributions || 0) > 0;

  const prob = data.probability_of_goal;
  const monthly = data.monthly_contribution || 0;
  const er = data.assumption?.expected_return ?? data.expected_return;
  const volSource = data.assumption?.volatility_source;

  const tooltip = (value: number, name: string) => {
    const labels: Record<string, string> = {
      p50: 'Median', p90: 'Optimistic (p90)', p10: 'Pessimistic (p10)', invested: 'Invested',
    };
    return [money(value, currency), labels[name] || name];
  };

  return (
    <div className="chart-card">
      <div className="d-flex justify-content-between align-items-baseline" style={{ marginBottom: 2 }}>
        <span className="fw-semibold" style={{ fontSize: '0.85rem', color: NAVY }}>
          Wealth projection
          <span style={{ color: GREY, fontWeight: 400 }}> · {data.horizon_years}y</span>
        </span>
        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: NAVY }}>
          {money(terminal.p50, currency)}
          <span style={{ color: GREY, fontWeight: 400, fontSize: '0.72rem' }}> median</span>
        </span>
      </div>

      <div style={{ fontSize: '0.72rem', color: GREY, marginBottom: 10 }}>
        {money(data.starting_value, currency)} today
        {monthly > 0 && <> · +{money(monthly, currency)}/mo</>}
        {' · '}{(er * 100).toFixed(1)}% expected return
        {volSource && <> · {(data.volatility * 100).toFixed(1)}% vol ({volSource})</>}
      </div>

      {rows.length > 1 ? (
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={rows} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke="#f1f1f3" />
            <XAxis dataKey="year" tickFormatter={yearLabel} tick={{ fontSize: 11, fill: '#71717a' }} minTickGap={24} />
            <YAxis tickFormatter={(v: number) => compact(v, currency)} tick={{ fontSize: 11, fill: '#71717a' }} width={52} />
            {/* Invisible base up to p10, then the visible p10–p90 band on top. */}
            <Area type="monotone" dataKey="p10" stackId="band" stroke="none" fill="none" isAnimationActive={false} />
            <Area type="monotone" dataKey="band" stackId="band" stroke="none" fill={BAND} fillOpacity={0.1} isAnimationActive={false} />
            <Line type="monotone" dataKey="p90" stroke={BAND} strokeOpacity={0.35} strokeWidth={1} dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="p10" stroke={BAND} strokeOpacity={0.35} strokeWidth={1} dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="p50" stroke={NAVY} strokeWidth={2} dot={false} isAnimationActive={false} />
            {/* Capital actually put in — the gap up to the median is growth. */}
            {hasContributions && (
              <Line type="monotone" dataKey="invested" stroke="#71717a" strokeWidth={1.5} strokeDasharray="3 3" dot={false} isAnimationActive={false} />
            )}
            {goal != null && (
              <ReferenceLine
                y={goal}
                stroke="#15803d"
                strokeDasharray="4 3"
                label={{ value: `Goal ${compact(goal, currency)}`, position: 'insideTopRight', fontSize: 11, fill: '#15803d' }}
              />
            )}
            <Tooltip
              formatter={tooltip as never}
              labelFormatter={(y: number) => (y === 0 ? 'Today' : `In ${y} years`)}
              contentStyle={{ fontSize: 12, borderRadius: 8 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      ) : (
        <div style={{ fontSize: '0.8rem', color: GREY }}>Not enough horizon to project.</div>
      )}

      {hasContributions && (
        <div style={{ fontSize: '0.72rem', color: GREY, marginTop: 8, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <span>
            <span style={{ display: 'inline-block', width: 14, borderTop: '1.5px dashed #71717a', verticalAlign: 'middle', marginRight: 5 }} />
            Invested {money(data.total_invested, currency)}
            <span style={{ color: '#c4c4c8' }}> ({money(data.starting_value, currency)} now + {money(data.total_contributions, currency)} saved)</span>
          </span>
          <span style={{ color: '#15803d', fontWeight: 600 }}>
            +{money(terminal.median_growth, currency)} median growth
          </span>
        </div>
      )}

      <div className="d-flex justify-content-between" style={{ fontSize: '0.72rem', color: GREY, marginTop: 8 }}>
        <span>Range in {data.horizon_years}y: {money(terminal.p10, currency)} – {money(terminal.p90, currency)}</span>
        {goal != null && prob != null && (
          <span style={{ color: prob >= 0.5 ? '#15803d' : NAVY, fontWeight: 600 }}>
            {Math.round(prob * 100)}% chance of reaching {compact(goal, currency)}
          </span>
        )}
      </div>
    </div>
  );
};

export default WealthProjectionChart;
